import { HF_REPO_ID_PATTERN } from "@inference-net/cf-compatible-models/training";
import { teamSearchSortSchema } from "./team-search-sort.models";
import { z } from "zod";

import type { L3KvBackend } from "../lib/models.engine-config.types";
import { ModelEngineConfigFileSchema } from "../lib/model-engine-config-file.models";
import { L3KvBackendSchema } from "../lib/models.engine-config.types";

/** ******************************************************************************
 *  Shared Enums
 ******************************************************************************* */

export const MODEL_LOCATIONS = ["hugging-face", "r2"] as const;
export const MODEL_MODALITIES = [
  "embeddings",
  "text",
  "image",
  "video",
  "audio",
] as const;

const modelLocationSchema = z.enum(MODEL_LOCATIONS);
const modelModalitySchema = z.enum(MODEL_MODALITIES);

/** ******************************************************************************
 *  Config File Schema
 ******************************************************************************* */

const modelEngineConfigFileSchema = ModelEngineConfigFileSchema;

/** ******************************************************************************
 *  Capabilities
 ******************************************************************************* */

/**
 * The caller's model-admin capabilities (`modelAdmin.getCapabilities`).
 * `isSuperAdmin` is derived from whether a super-admin actor resolved for the
 * request; `email` is that actor's email, null for a non-super-admin caller.
 * `canManagePromoCodes` is true for super-admins and for verified staff holding
 * the `promo-code-manager` role.
 */
export const modelCapabilitiesSchema = z.object({
  isSuperAdmin: z.boolean(),
  email: z.string().nullable(),
  canUseGravityWebhookTests: z.boolean(),
  canManagePromoCodes: z.boolean(),
});

/** ******************************************************************************
 *  List Models
 ******************************************************************************* */

export const listModelsParams = z.object({
  cursor: z
    .object({
      pageSize: z.number().int().min(1).max(100).default(50),
      offset: z.number().int().min(0).default(0),
    })
    .optional(),
});

export type ListModelsParams = z.infer<typeof listModelsParams>;

/** ******************************************************************************
 *  Create Model
 ******************************************************************************* */

export const createModelInput = z.object({
  canonicalAlias: z.string().min(1).optional(),
  name: z.string(),
  ownerTeamId: z.string(),
  ownerProjectId: z.string().nullish(),
  baseModelId: z.string().nullish(),
  isTrainable: z.boolean().default(false),
  isJudgeModel: z.boolean().default(false),
  isHaloModel: z.boolean().default(false),
  weightsLocation: modelLocationSchema.nullish(),
  huggingFaceId: z.string().nullish(),
  r2WeightsPath: z.string().nullish(),
  parameters: z.string(),
  quantization: z.string(),
  inputModalities: z.array(modelModalitySchema),
  outputModalities: z.array(modelModalitySchema),
  creator: z.string(),
  responseStructuredOutputsSupported: z.boolean().default(false),
  responseJsonObjectSupported: z.boolean().default(false),
  toolsSupported: z.boolean().default(false),
  reasoningSupported: z.boolean().default(false),
  /** Teams need an approved access request before calling the model. */
  requiresApprovedAccess: z.boolean().default(false),
  maxContextSize: z.number().int(),
  maxOutputTokens: z.number().int(),
});

export type CreateModelInput = z.infer<typeof createModelInput>;

/** ******************************************************************************
 *  Update Model
 ******************************************************************************* */

export const updateModelInput = z.object({
  modelId: z.string(),
  data: createModelInput.partial().extend({
    /** YYYY-MM-DD public release date; drives the catalog's newest-first sort. */
    releaseDate: z.string().date().nullable().optional(),
  }),
});

export type UpdateModelInput = z.infer<typeof updateModelInput>;

/**
 * The metadata a model's owning team may edit after creation. Weights
 * (`huggingFaceId`, `r2WeightsPath`, `weightsLocation`), ownership, lineage,
 * and the platform-only flags are all fixed here — changing them would
 * repoint an existing model at different weights or move it between teams.
 */
export const updateTeamModelInput = z.object({
  modelId: z.string(),
  data: createModelInput
    .pick({
      name: true,
      creator: true,
      parameters: true,
      quantization: true,
      inputModalities: true,
      outputModalities: true,
      reasoningSupported: true,
      toolsSupported: true,
      responseJsonObjectSupported: true,
      responseStructuredOutputsSupported: true,
      maxContextSize: true,
      maxOutputTokens: true,
    })
    .partial(),
});

export type UpdateTeamModelInput = z.infer<typeof updateTeamModelInput>;

/**
 * Register a team-owned model plus the engine config that makes it
 * deployable. Weights come from Hugging Face; the optional token is stored as
 * `HF_TOKEN` on the created config rather than on the model.
 */
export const registerTeamModelInput = z.object({
  model: createModelInput
    .pick({
      name: true,
      creator: true,
      parameters: true,
      quantization: true,
      inputModalities: true,
      outputModalities: true,
      maxContextSize: true,
      maxOutputTokens: true,
      reasoningSupported: true,
      toolsSupported: true,
      responseJsonObjectSupported: true,
      responseStructuredOutputsSupported: true,
    })
    // Team-chosen, and interpolated into Hub API URLs, the `hf download` argv
    // and model-cache paths on the worker: only a plain `org/name` is allowed.
    .extend({
      huggingFaceId: z.string().trim().regex(HF_REPO_ID_PATTERN, {
        message:
          "Use a Hugging Face repo id like org/name (no URL, path or host)",
      }),
    }),
  engineConfig: z.object({
    engine: z.string().min(1),
    cards: z.record(z.string(), z.number()),
  }),
  huggingFaceToken: z.string().optional(),
});

export type RegisterTeamModelInput = z.infer<typeof registerTeamModelInput>;

/** ******************************************************************************
 *  List Engine Configs
 ******************************************************************************* */

export const listEngineConfigsParams = z.object({
  modelId: z.string(),
});

export type ListEngineConfigsParams = z.infer<typeof listEngineConfigsParams>;

/** ******************************************************************************
 *  Create Engine Config
 ******************************************************************************* */

export const createEngineConfigInput = z.object({
  modelId: z.string(),
  engine: z.string(),
  name: z.string().nullish(),
  cards: z.record(z.string(), z.number()),
  tags: z.record(z.string(), z.string().nullable()).default({}),
  flags: z.record(z.string(), z.string()),
  configFiles: z.array(modelEngineConfigFileSchema),
  environmentVariables: z.record(z.string(), z.string()),
  parallelism: z.number().int(),
  modelInitializationTimeoutMs: z.number().int().nullish(),
  /** The L3 KV store the engine runs on; null = no L3. See {@link L3_KV_BACKEND_ENGINES}. */
  l3KvBackend: L3KvBackendSchema.nullable().default(null),
});

export type CreateEngineConfigInput = z.infer<typeof createEngineConfigInput>;

/** ******************************************************************************
 *  Update Engine Config
 ******************************************************************************* */

export const updateEngineConfigInput = z.object({
  configId: z.string(),
  data: createEngineConfigInput
    .omit({
      modelId: true,
    })
    .partial(),
});

export type UpdateEngineConfigInput = z.infer<typeof updateEngineConfigInput>;

/** ******************************************************************************
 *  Config Version History
 ******************************************************************************* */

export const getConfigVersionHistoryParams = z.object({
  configId: z.string(),
});

export type GetConfigVersionHistoryParams = z.infer<
  typeof getConfigVersionHistoryParams
>;

export const getConfigVersionParams = z.object({
  configId: z.string(),
  version: z.number().int(),
});

export type GetConfigVersionParams = z.infer<typeof getConfigVersionParams>;

export const getConfigLatestParams = z.object({
  configId: z.string(),
});

export type GetConfigLatestParams = z.infer<typeof getConfigLatestParams>;

/** ******************************************************************************
 *  Engine Config Notes
 ******************************************************************************* */

/** Markdown notes are capped so a note stays a note, not an attachment. */
export const ENGINE_CONFIG_NOTE_MAX_LENGTH = 100_000;

export const addConfigNoteInput = z.object({
  configId: z.string(),
  /** Omit for the note on the config as a whole. */
  configVersion: z.number().int().positive().nullish(),
  body: z.string().max(ENGINE_CONFIG_NOTE_MAX_LENGTH),
});

export type AddConfigNoteInput = z.infer<typeof addConfigNoteInput>;

export const getConfigNotesParams = z.object({
  configId: z.string(),
});

export type GetConfigNotesParams = z.infer<typeof getConfigNotesParams>;

/** ******************************************************************************
 *  Delete Engine Config
 ******************************************************************************* */

export const deleteEngineConfigParams = z.object({
  configId: z.string(),
});

export type DeleteEngineConfigParams = z.infer<typeof deleteEngineConfigParams>;

/** ******************************************************************************
 *  Get Model
 ******************************************************************************* */

export const getModelParams = z.object({
  modelId: z.string(),
});

export type GetModelParams = z.infer<typeof getModelParams>;

/** ******************************************************************************
 *  Search Teams
 ******************************************************************************* */

/**
 * Default page size for paginated team search. Single source of truth shared by
 * the client (infinite-query page size + "has more" check), the request schema
 * default, and the service default so they can't drift out of sync.
 */
export const TEAM_SEARCH_PAGE_SIZE = 50;

export const searchTeamsParams = z.object({
  query: z.string().default(""),
  /**
   * Offset-based pagination. Optional/nullish so existing callers (and tRPC's
   * useInfiniteQuery, which injects `cursor`) keep working; defaults match the
   * previous fixed page of 50.
   */
  cursor: z
    .object({
      pageSize: z.number().int().min(1).max(100).default(TEAM_SEARCH_PAGE_SIZE),
      offset: z.number().int().min(0).default(0),
    })
    .nullish(),
  sort: teamSearchSortSchema.nullish(),
});

export type SearchTeamsParams = z.infer<typeof searchTeamsParams>;

/** ******************************************************************************
 *  Field Diff
 ******************************************************************************* */

export type FieldDiff = {
  field: string;
  from?: unknown;
  to?: unknown;
  type: "added" | "removed" | "changed";
};

/** ******************************************************************************
 *  Return Types
 ******************************************************************************* */

export type ModelLocation = (typeof MODEL_LOCATIONS)[number];
export type ModelModality = (typeof MODEL_MODALITIES)[number];

export type ModelRecord = {
  id: string;
  canonicalAlias: string;
  name: string;
  ownerTeamId: string;
  ownerProjectId: string | null;
  baseModelId: string | null;
  isTrainable: boolean;
  isJudgeModel: boolean;
  isHaloModel: boolean;
  weightsLocation: ModelLocation | null;
  huggingFaceId: string | null;
  r2WeightsPath: string | null;
  parameters: string | null;
  quantization: string | null;
  inputModalities: ModelModality[];
  outputModalities: ModelModality[];
  creator: string;
  responseStructuredOutputsSupported: boolean;
  responseJsonObjectSupported: boolean;
  toolsSupported: boolean;
  reasoningSupported: boolean;
  logprobsSupported?: boolean;
  requiresApprovedAccess: boolean;
  maxContextSize: number;
  maxOutputTokens: number;
  createdByUserId: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt: Date | null;
};

export type ModelRecordWithCreator = ModelRecord & {
  createdByUser: {
    id: string;
    name: string;
    email: string | null;
  } | null;
};

export type EngineConfigFile = z.infer<typeof modelEngineConfigFileSchema>;

export type EngineConfigRecord = {
  configId: string;
  version: number;
  modelId: string;
  engine: string;
  name: string | null;
  cards: Record<string, number>;
  tags: Record<string, string | null>;
  flags: Record<string, string>;
  configFiles: EngineConfigFile[];
  environmentVariables: Record<string, string>;
  parallelism: number;
  modelInitializationTimeoutMs: number | null;
  l3KvBackend: L3KvBackend | null;
  createdByUserId: string;
  createdAt: Date;
  updatedAt: Date;
};

/** ******************************************************************************
 *  List Team Models (deployable models with weights)
 ******************************************************************************* */

export const listTeamModelsParams = z.object({
  teamId: z.string(),
});
export type ListTeamModelsParams = z.infer<typeof listTeamModelsParams>;

/** ******************************************************************************
 *  Service Interface
 ******************************************************************************* */

export type ModelAdminServiceInterface = {
  listModels(params: ListModelsParams): Promise<{
    models: ModelRecord[];
    total: number;
  }>;
  createModel(params: {
    input: CreateModelInput;
    userId: string;
  }): Promise<ModelRecord>;
  updateModel(params: {
    input: UpdateModelInput;
    userId: string;
  }): Promise<ModelRecord>;
  listEngineConfigs(
    params: ListEngineConfigsParams,
  ): Promise<EngineConfigRecord[]>;
  createEngineConfig(params: {
    input: CreateEngineConfigInput;
    userId: string;
  }): Promise<EngineConfigRecord>;
  updateEngineConfig(params: {
    input: UpdateEngineConfigInput;
    userId: string;
  }): Promise<EngineConfigRecord>;
  getConfigVersionHistory(params: GetConfigVersionHistoryParams): Promise<
    {
      version: EngineConfigRecord;
      diffs: FieldDiff[];
    }[]
  >;
  getConfigVersion(params: GetConfigVersionParams): Promise<EngineConfigRecord>;
  getConfigLatest(params: GetConfigLatestParams): Promise<EngineConfigRecord>;
  deleteEngineConfig(params: DeleteEngineConfigParams): Promise<void>;
  getModel(params: GetModelParams): Promise<ModelRecordWithCreator | null>;
  listTeamModels(params: ListTeamModelsParams): Promise<ModelRecord[]>;
  searchTeams(
    params: SearchTeamsParams,
  ): Promise<{ id: string; name: string; slug: string }[]>;
};
